/**
 * Mode-aware class mutations for the collection color-filter, shared by the two
 * editors (SimpleEditor/MatrixEditor) and the cost summary.
 *
 * ## Why this isn't inline in each component
 *
 * Adding or removing a class is not a single-field edit in matrix mode. Matrix
 * classes are the *columns* of the threshold grid, so every band's
 * `thresholds[i].boundaries` array has one entry per class and has to be kept the
 * same length as `classes`. Miss that and the filter looks fine but misbehaves:
 * a class with no boundary in any band can never be matched, and a stale
 * boundary left behind by a delete shifts every subsequent class's range.
 *
 * Simple mode has the opposite shape — each class carries its own `[min, max)`
 * and there is no grid to keep in step.
 *
 * Three components now offer these actions, so the branch lives here once rather
 * than being re-derived (and eventually diverging) in each of them. Each function
 * is pure and returns a partial filter patch for the caller to hand to
 * `updateFilter`, matching how the rest of the filter config is edited.
 *
 * @module classMutations
 */

import { createClassId } from './classId';

// A new class's starting appearance — the same values both editors used inline
// before this module existed, kept identical so "Add class" is unchanged.
const NEW_CLASS_COLOR = '#4ade80';
const NEW_CLASS_OPACITY = 0.7;

const isSimpleFilter = filter => filter?.type === 'simple';

/**
 * Build the class object a new row starts from. Exported for callers that need
 * the seed values before the patch is applied (the cost summary opens the new
 * row in edit mode, so it needs the draft up front).
 *
 * @param {object} filter - active matrix/simple filter config
 * @returns {object} a new class, shaped for the filter's mode
 */
export const createClass = filter => {
	const classes = filter?.classes ?? [];
	const simple = isSimpleFilter(filter);
	return {
		// Prefix kept per-mode ('class'/'mclass') so ids stay distinguishable by
		// editor type in saved and exported filter configs.
		id: createClassId(simple ? 'class' : 'mclass'),
		label: `Class ${classes.length + 1}`,
		color: NEW_CLASS_COLOR,
		opacity: NEW_CLASS_OPACITY,
		pricePerM2: 0,
		// Simple classes carry their own range; matrix classes are bounded by the
		// threshold grid instead, so they carry none.
		...(simple ? { min: 0, max: null } : {}),
	};
};

/**
 * Append a class.
 *
 * @param {object} filter - active matrix/simple filter config
 * @returns {{ patch: object, newClass: object }} the partial filter update, plus
 *   the created class so the caller can focus/edit it without re-finding it
 */
export const buildAddClassPatch = filter => {
	const newClass = createClass(filter);
	const patch = { classes: [...(filter?.classes ?? []), newClass] };

	if (!isSimpleFilter(filter)) {
		// Give the new column a boundary in every band, seeded from that band's
		// max — otherwise the class exists but nothing can ever fall into it. An
		// open-ended band (max: null) has no max, so the class starts empty at the
		// band's top boundary (or its min) to keep the run monotonic.
		patch.thresholds = (filter?.thresholds ?? []).map(threshold => ({
			...threshold,
			boundaries: [
				...(threshold.boundaries ?? []),
				threshold.max ?? threshold.boundaries?.at(-1) ?? threshold.min ?? 0,
			],
		}));
	}

	return { patch, newClass };
};

/**
 * Remove the class at `index`.
 *
 * @param {object} filter - active matrix/simple filter config
 * @param {number} index - position in `filter.classes`
 * @returns {object} the partial filter update
 */
export const buildRemoveClassPatch = (filter, index) => {
	const patch = { classes: (filter?.classes ?? []).filter((_, i) => i !== index) };

	if (!isSimpleFilter(filter)) {
		// Drop the matching column from every band, or the remaining classes
		// silently inherit the wrong boundaries.
		patch.thresholds = (filter?.thresholds ?? []).map(threshold => ({
			...threshold,
			boundaries: (threshold.boundaries ?? []).filter((_, i) => i !== index),
		}));
	}

	return patch;
};

/**
 * Merge `changes` into the class at `index`. Mode-independent — editing a class
 * in place never changes how many classes there are, so the threshold grid is
 * unaffected.
 *
 * @param {object} filter - active matrix/simple filter config
 * @param {number} index - position in `filter.classes`
 * @param {object} changes - partial class fields to merge
 * @returns {object} the partial filter update
 */
export const buildUpdateClassPatch = (filter, index, changes) => ({
	classes: (filter?.classes ?? []).map((cls, i) => (i === index ? { ...cls, ...changes } : cls)),
});
